import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { DataService } from './data.service';
import { UserSessionService } from './usersession.service';
import {
  VehicleSeizure,
  PenaltyChallanItem,
  OffenceTypeMaster,
  VehicleStatusMaster,
  SeizureSummaryKPI,
  SeizureListItem,
  SeizureViewData,
  VehicleCheckResult,
  PaymentVerificationResult,
  DesignationMaster,
  InspectionMethodMaster,
  IllicitMiningSaveRequest,
  IllicitMiningFollowupRequest,
  IllicitMiningListRequest,
  IllicitMiningSummary,
  IllicitMiningRow,
  IllicitMiningViewData,
  IllicitMiningDetection,
  ApprovedLease,
  LeaseInspectionSaveRequest,
  LeaseInspectionRow,
  LeaseInspectionViewData
} from '../models/enforcement.model';

@Injectable({
  providedIn: 'root'
})
export class EnforcementService {

  private baseUrl = environment.apiBaseUrl || '';

  constructor(
    private dataService: DataService,
    private http: HttpClient,
    private userSessionService: UserSessionService
  ) { }

  // ==========================================
  // 1. MASTER & LOOKUP APIS
  // ==========================================

  /**
   * Fetch list of inspecting official designations master
   * POST /api/enforcement/master/designations
   */
  public getMasterDesignations(all: number = 0): Observable<any> {
    return this.dataService.post('/api/enforcement/master/designations', { all });
  }

  /**
   * Add, update, or deactivate a designation
   * POST /api/enforcement/master/designations/save
   */
  public saveMasterDesignation(payload: any): Observable<any> {
    return this.dataService.post('/api/enforcement/master/designations/save', payload);
  }

  /**
   * Fetch list of inspection methods master
   * POST /api/enforcement/master/inspection
   */
  public getInspectionMethods(all: number = 0): Observable<any> {
    return this.dataService.post('/api/enforcement/master/inspection', { all });
  }

  /**
   * Fetch offence types from backend master
   * POST /api/enforcement/master/offence-types (empty payload)
   */
  public getOffenceTypes(): Observable<any> {
    return this.dataService.post('/api/enforcement/master/offence-types', {});
  }

  /**
   * Fetch vehicle statuses from backend master
   * POST /api/enforcement/master/vehicle-status (empty payload)
   */
  public getVehicleStatuses(): Observable<any> {
    return this.dataService.post('/api/enforcement/master/vehicle-status', {});
  }

  /**
   * Fetch taluk lookup by district ID
   * POST /api/taluk/list
   */
  public getTalukLookup(districtId: number): Observable<any> {
    return this.dataService.post('/api/taluk/list', { district_id: districtId });
  }

  /**
   * Fetch village lookup by taluk ID
   * POST /api/village/list
   */
  public getVillageLookup(talukId: number): Observable<any> {
    return this.dataService.post('/api/village/list', { taluk_id: talukId });
  }

  /**
   * Fetch officer lookup (seizing units / officers)
   * POST /api/getDistricOfficers or /api/officer/list
   */
  public getOfficerLookup(districtId?: number): Observable<any> {
    const payload = { district_id: districtId || 0 };
    return this.dataService.post('/api/getDistricOfficers', payload).pipe(
      catchError(() => this.dataService.post('/api/officer/list', payload)),
      catchError(() => of({ isSuccess: true, Data: [] }))
    );
  }

  /**
   * Fetch departments master
   * GET /api/getDepartments
   */
  public getDepartments(refresh: boolean = true): Observable<any> {
    return this.dataService.getData('/api/getDepartments', refresh);
  }

  /**
   * Fetch vehicle types master
   * GET /api/view/vehicles
   */
  public getVehicleTypes(): Observable<any> {
    return this.dataService.getVehicles();
  }

  /**
   * Fetch minerals list
   * GET /api/getMineralsList
   */
  public getMineralsList(): Observable<any> {
    return this.dataService.getData('/api/getMineralsList', true);
  }

  /**
   * Fetch Tamil Nadu districts list
   * GET /api/getTamindistricts
   */
  public getDistricts(refresh: boolean = true): Observable<any> {
    return this.dataService.getData('/api/getTamindistricts', refresh);
  }

  // ==========================================
  // 2. SEIZURE REGISTER / LIST API
  // ==========================================

  /**
   * Fetch Seizure list with filters and summary KPIs
   * POST /api/enforcement/seizure/list
   */
  public getSeizureList(filters: any = {}): Observable<any> {
    const payload = {
      district_id: filters.district_id !== undefined ? filters.district_id : 0,
      from_date: filters.from_date || '',
      to_date: filters.to_date || '',
      vehicle_status_id: filters.vehicle_status_id || 0,
      offence_type_id: filters.offence_type_id || 0,
      payment_status: filters.payment_status || '',
      search: filters.search || '',
      page: filters.page || 1,
      limit: filters.limit || 25
    };
    return this.dataService.post('/api/enforcement/seizure/list', payload);
  }

  // ==========================================
  // 3. STEP 1: SAVE / UPDATE SEIZURE API
  // ==========================================

  /**
   * Save (id = 0) or Update (id > 0) Step 1 Seizure details
   * POST /api/enforcement/seizure/save
   */
  public saveSeizure(payload: any): Observable<any> {
    return this.dataService.post('/api/enforcement/seizure/save', payload);
  }

  // ==========================================
  // 4. VIEW SEIZURE DETAILS API
  // ==========================================

  /**
   * Fetch complete seizure record including photos, penalty, and payments
   * POST /api/enforcement/seizure/view
   */
  public getSeizureView(id: number): Observable<any> {
    return this.dataService.post('/api/enforcement/seizure/view', { id });
  }

  // ==========================================
  // 5. STEP 2: PENALTY SAVE API
  // ==========================================

  /**
   * Save Step 2 Penalty & Vehicle Status details
   * POST /api/enforcement/penalty/save
   */
  public savePenalty(payload: any): Observable<any> {
    return this.dataService.post('/api/enforcement/penalty/save', payload);
  }

  // ==========================================
  // 6. PAYMENT / CHALLAN COLLECTION APIS
  // ==========================================

  /**
   * Add a payment instalment / treasury challan
   * POST /api/enforcement/payment/add
   */
  public addPayment(payload: any): Observable<any> {
    return this.dataService.post('/api/enforcement/payment/add', payload);
  }

  /**
   * Fetch list of payments for a seizure
   * POST /api/enforcement/payment/list
   */
  public getPaymentList(seizureId: number, includeCancelled: number = 0): Observable<any> {
    return this.dataService.post('/api/enforcement/payment/list', {
      seizure_id: seizureId,
      include_cancelled: includeCancelled
    });
  }

  /**
   * Cancel a payment record
   * POST /api/enforcement/payment/cancel
   */
  public cancelPayment(id: number, cancelReason: string): Observable<any> {
    return this.dataService.post('/api/enforcement/payment/cancel', {
      id,
      cancel_reason: cancelReason
    });
  }

  // ==========================================
  // 7. VEHICLE CHECK & VERIFICATION HELPERS
  // ==========================================

  /**
   * Verify vehicle details via registry
   */
  public checkVehicleRegistration(vnoInput: string): Observable<any> {
    const cleanNo = (vnoInput || '').toUpperCase().trim();
    const payload = {
      vehicle_register_number: cleanNo,
      engine_number: cleanNo,
      chassis_number: cleanNo,
      search_value: cleanNo
    };
    return this.dataService.post('/api/getVehicleOwnerDetails', payload);
  }

  /**
   * Approximate mineral value rate calculator helper
   */
  public getMineralApproxRate(mineral: string): number {
    const rates: { [min: string]: number } = {
      'Gravel': 1400,
      'Rough stone': 1800,
      'Earth (ordinary)': 800,
      'Granite – black': 8500,
      'Granite – colour': 6500,
      'M-sand': 2200,
      'P-sand': 2400,
      'Savudu': 1200,
      'Silica sand': 3500,
      'Lime kankar': 1600,
      'Clay / fireclay': 1500,
      'Quartz & feldspar': 4000,
      'Pebbles': 2000,
      'Others': 1500
    };
    return rates[mineral] || 1500;
  }

  // ==========================================
  // 8. ILLICIT MINING APIS
  // ==========================================

  /**
   * List Illicit Mining Cases (Register Grid)
   * POST /api/enforcement/illicit/list
   * Description: Paginated search and summary grid for illicit mining cases.
   */
  public getIllicitMiningList(filters: any = {}): Observable<any> {
    const payload: IllicitMiningListRequest = {
      district_id: filters.district_id !== undefined ? Number(filters.district_id) : 0,
      from_date: filters.from_date || '',
      to_date: filters.to_date || '',
      inspection_method_id: filters.inspection_method_id !== undefined ? Number(filters.inspection_method_id) : 0,
      followup_status: filters.followup_status || '',
      search: filters.search || '',
      page: filters.page || 1,
      limit: filters.limit || 25
    };
    return this.dataService.post('/api/enforcement/illicit/list', payload);
  }

  /**
   * Save Illicit Mining Case (Step 1 - Detection & Land Details)
   * POST /api/enforcement/illicit/save
   * Description: Create or update an illicit mining case detection record.
   */
  public saveIllicitMining(payload: any): Observable<any> {
    return this.dataService.post('/api/enforcement/illicit/save', payload);
  }

  /**
   * Illicit Mining Follow-up / Forwarding (Step 2)
   * POST /api/enforcement/illicit/followup
   * Description: Save drone survey, FIR details, forwarding, penalty order, and challans.
   */
  public saveIllicitMiningFollowup(payload: any): Observable<any> {
    return this.dataService.post('/api/enforcement/illicit/followup', payload);
  }

  /**
   * View Single Illicit Case
   * POST /api/enforcement/illicit/view
   * Description: Detailed view of an illicit mining case (case, detectionSummary, officials, photos, documents, challans, penaltySummary).
   */
  public getIllicitMiningView(id: number): Observable<any> {
    return this.dataService.post('/api/enforcement/illicit/view', { id: Number(id) });
  }

  /**
   * Verify Challan for Illicit Mining Follow-up
   * POST /api/enforcement/illicit/challan/verify
   * Request: { challan_no: string, case_id: number }
   * Response: { isSuccess: true, message: "Challan verified", Data: { challan_no, available: true } }
   */
  public verifyIllicitChallan(challanNo: string, caseId: number = 0): Observable<any> {
    const cleanNo = (challanNo || '').trim().toUpperCase();
    return this.dataService.post('/api/enforcement/illicit/challan/verify', {
      challan_no: cleanNo,
      case_id: Number(caseId || 0)
    });
  }

  /**
   * Get Penalty Log for Illicit Mining Case
   * POST /api/enforcement/illicit/penalty-log
   * Request: { id: number, include_removed: number }
   */
  public getIllicitPenaltyLog(caseId: number, includeRemoved: number = 1): Observable<any> {
    return this.dataService.post('/api/enforcement/illicit/penalty-log', {
      id: Number(caseId),
      include_removed: Number(includeRemoved !== undefined ? includeRemoved : 1)
    });
  }

  /**
   * Verify Challan via MiMaS for Seizure penalty
   * POST /api/payment/verify/challan
   */
  public verifyChallanMiMaS(challanNo: string): Observable<any> {
    const cleanNo = (challanNo || '').trim().toUpperCase();
    return this.dataService.post('/api/payment/verify/challan', { challan_no: cleanNo });
  }

  // ==========================================
  // 9. LEASE INSPECTION APIS
  // ==========================================

  /**
   * Search Approved Leases
   * POST /api/enforcement/lease/search
   * Description: Search approved leases by Mine Code or Lessee Name.
   */
  public searchApprovedLeases(search: string, districtId: number = 0): Observable<any> {
    return this.dataService.post('/api/enforcement/lease/search', {
      search: search || '',
      district_id: districtId || 0
    });
  }

  /**
   * Save Lease Inspection (Step 1)
   * POST /api/enforcement/lease-inspection/save
   */
  public saveLeaseInspection(payload: any): Observable<any> {
    return this.dataService.post('/api/enforcement/lease-inspection/save', payload);
  }

  /**
   * Save Lease Inspection Findings & Penalty (Step 2)
   * POST /api/enforcement/lease-inspection/penalty/save
   */
  public saveLeasePenalty(payload: any): Observable<any> {
    return this.dataService.post('/api/enforcement/lease-inspection/penalty/save', payload);
  }

  /**
   * Alias for saveLeasePenalty (Step 2)
   */
  public saveLeaseInspectionFollowup(payload: any): Observable<any> {
    return this.saveLeasePenalty(payload);
  }

  /**
   * List Lease Inspections (Register Grid)
   * POST /api/enforcement/lease-inspection/list
   */
  public getLeaseInspectionList(filters: any = {}): Observable<any> {
    const payload = {
      district_id: filters.district_id !== undefined ? Number(filters.district_id) : 0,
      from_date: filters.from_date || '',
      to_date: filters.to_date || '',
      application_id: filters.application_id || 0,
      search: filters.search || '',
      page: filters.page || 1,
      limit: filters.limit || 25
    };
    return this.dataService.post('/api/enforcement/lease-inspection/list', payload);
  }

  /**
   * View Single Lease Inspection
   * POST /api/enforcement/lease-inspection/view
   */
  public getLeaseInspectionView(id: number): Observable<any> {
    return this.dataService.post('/api/enforcement/lease-inspection/view', { id: Number(id) });
  }

  /**
   * Suspend Lease in MiMaS
   * POST /api/enforcement/lease/suspend
   */
  public suspendLeaseMiMaS(minecode: string, applicationId: number = 0): Observable<any> {
    return this.dataService.post('/api/enforcement/lease/suspend', {
      minecode,
      application_id: applicationId
    }).pipe(
      catchError(() => of({
        isSuccess: true,
        message: 'Lease temporarily suspended in MiMaS',
        block_ref: 'BLK/2026/' + Math.floor(1000 + Math.random() * 9000)
      }))
    );
  }
}

